import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import styles from './Paywall.module.scss';

import { useSubscription } from '../../features/subscription/SubscriptionProvider';
import { useAllRecipes } from '../../sanity/hooks';
import { useSiteSettings } from '../../sanity/hooks';

import ArrowIcon from '../_shared/icons/ArrowIcon';
import RecipeList from '../RecipeList/RecipeList';
import Spinner from '../_shared/Spinner/Spinner';

const Paywall = ({ returnTo }) => {
  const navigate = useNavigate();
  const { data: allRecipes } = useAllRecipes();
  const { data: siteSettings } = useSiteSettings();
  const {
    getOfferings,
    purchasePackage,
    restorePurchases,
    isLoading,
    offerings,
    isNativePlatform,
  } = useSubscription();

  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [message, setMessage] = useState(null);

  // Default redirect destination
  const redirectTo = returnTo || '/recipes';

  useEffect(() => {
    if (isNativePlatform) {
      getOfferings();
    }
  }, [isNativePlatform, getOfferings]);

  const freeRecipes = useMemo(() => {
    if (!allRecipes?.length) return [];
    return allRecipes.filter((recipe) => !!recipe.isFree);
  }, [allRecipes]);

  const handlePurchase = async (pkg) => {
    setPurchasing(true);
    setMessage(null);
    const result = await purchasePackage(pkg);
    setPurchasing(false);

    if (result.cancelled) {
      setMessage('Kjøp avbrutt');
    } else if (result.error) {
      setMessage('Noe gikk galt. Prøv igjen.');
    } else if (result.success) {
      navigate(redirectTo);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setMessage(null);
    const result = await restorePurchases();
    setRestoring(false);

    if (result.success) {
      if (result.hasSubscription) {
        navigate(redirectTo);
      } else {
        setMessage('Ingen tidligere kjøp funnet');
      }
    } else {
      setMessage('Kunne ikke gjenopprette. Prøv igjen.');
    }
  };

  const currentOffering = offerings?.current;

  return (
    <motion.main
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={styles.mainContainer}
    >
      <Link to={'/recipes'} className={styles.linkBack}>
        <span className={styles.arrowIconContainer}>
          <ArrowIcon direction="left" alt="" />
        </span>
        <span className={styles.linkBackText}>Til forsiden</span>
      </Link>

      <h1 className={styles.header}>
        {siteSettings?.paywallContent.paywallTitle}
      </h1>

      <p className={styles.text} style={{ maxWidth: '200px', marginLeft: 'auto', marginRight: 'auto' }}>
        {siteSettings?.paywallContent.paywallText}
      </p>

      <section className={styles.linksContainer}>
        {!isNativePlatform ? (
          <p className={styles.text}>Last ned appen for å abonnere</p>
        ) : isLoading || purchasing ? (
          <Spinner />
        ) : currentOffering ? (
          <>
            {currentOffering.availablePackages.map((pkg) => (
              <button
                key={pkg.identifier}
                onClick={() => handlePurchase(pkg)}
                className={styles.membership}
                disabled={purchasing}
              >
                {pkg.product.title} - {pkg.product.priceString}
              </button>
            ))}
          </>
        ) : (
          <p className={styles.text}>Kunne ikke laste abonnementer</p>
        )}

        {isNativePlatform && (
          <button
            onClick={handleRestore}
            className={styles.login}
            disabled={restoring || purchasing}
          >
            {restoring ? 'Gjenoppretter...' : 'Gjenopprett kjøp'}
          </button>
        )}

        {message && <p className={styles.text}>{message}</p>}

        {isNativePlatform && currentOffering && (
          <p className={styles.legalDisclaimer}>
            Ved å abonnere godtar du våre{' '}
            <a
              href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Vilkår
            </a>{' '}
            og{' '}
            <a
              href="https://cheffihub.com/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Personvern
            </a>
          </p>
        )}
      </section>

      {!!freeRecipes?.length ? (
        <section className={styles.otherRecipesContainer}>
          <h2 className={styles.sectionHeader}>Test våre gratis oppskrifter</h2>
          <RecipeList recipes={freeRecipes} />
        </section>
      ) : null}
    </motion.main>
  );
};

export default Paywall;
